import { useEffect, useMemo, useState } from 'react'
import GShell from './GShell.jsx'
import { Title, Card, Pill, Modal, Field, Err, money, fmtDate } from './gui.jsx'
import { watchVendors } from '../../lib/vendors.js'
import { watchProducts } from '../../lib/products.js'
import { PO_STATUSES, watchPurchaseOrders, createPO, emailPO, cancelPO, deletePO, receivePO } from '../../lib/purchaseOrders.js'

const poTotal = (po) => Object.values(po.lines || {}).reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.cost) || 0), 0)
const statusTint = (s) => ({ Draft: { bg: 'rgba(107,114,128,0.12)', fg: '#4B5563' }, Ordered: { bg: 'rgba(37,99,235,0.12)', fg: '#1D4ED8' }, 'Partially Received': { bg: 'rgba(var(--ge-primary-rgb),0.2)', fg: 'var(--ge-primary)' }, Received: { bg: 'rgba(var(--ge-primary-rgb),0.14)', fg: 'var(--ge-primary)' }, Cancelled: { bg: 'rgba(185,28,28,0.12)', fg: '#B91C1C' } }[s] || {})

export default function GPurchaseOrders() {
  const [raw, setRaw] = useState({})
  const [vendors, setVendors] = useState({})
  const [products, setProducts] = useState({})
  const [creating, setCreating] = useState(false)
  const [receiveFor, setReceiveFor] = useState(null)
  useEffect(() => { const a = watchPurchaseOrders(setRaw), b = watchVendors(setVendors), c = watchProducts(setProducts); return () => { a(); b(); c() } }, [])

  const rows = useMemo(() => Object.values(raw || {}).filter(Boolean).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)), [raw])
  const vname = (id) => vendors[id]?.name || '(no vendor)'

  return (
    <GShell>
      <section>
        <Title title="Purchase Orders" subtitle="Reorder from vendors — receiving updates inventory." right={<button className="btn-primary text-xs" onClick={() => setCreating(true)}>+ New PO</button>} />
      </section>

      <section className="pb-4">
        {rows.length === 0 ? <p className="text-sm text-gray-500">No purchase orders yet.</p> : (
          <div className="grid md:grid-cols-2 gap-3">
            {rows.map((po) => {
              const t = statusTint(po.status); const lines = Object.values(po.lines || {})
              return (
                <Card key={po.id}>
                  <div className="flex items-start justify-between gap-2">
                    <div><div className="font-extrabold" style={{ color: 'var(--ge-primary)' }}>{vname(po.vendorId)}</div>
                      <div className="text-xs text-gray-500">{lines.length} line{lines.length === 1 ? '' : 's'} · {money(poTotal(po))}{po.expectedDate ? ` · exp ${fmtDate(po.expectedDate)}` : ''}</div></div>
                    <Pill bg={t.bg} fg={t.fg}>{po.status}</Pill>
                  </div>
                  <div className="text-xs text-gray-600 mt-2 space-y-0.5 max-h-24 overflow-y-auto">
                    {lines.map((l, i) => <div key={i} className="flex justify-between"><span className="truncate">{l.name || '(item)'}</span><span className="shrink-0 text-gray-400">{l.received || 0}/{l.qty} @ {money(l.cost)}</span></div>)}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1 pt-2 border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                    {po.status === 'Draft' ? <button className="btn-primary text-[11px]" onClick={() => emailPO(po.id)}>📧 Email → Ordered</button> : null}
                    {['Ordered', 'Partially Received'].includes(po.status) ? <button className="btn-primary text-[11px]" onClick={() => setReceiveFor(po)}>Receive</button> : null}
                    <button className="btn-ghost text-[11px]" onClick={() => printPO(po, vname(po.vendorId))}>Print</button>
                    {!['Received', 'Cancelled'].includes(po.status) ? <button className="btn-ghost text-[11px]" onClick={() => cancelPO(po.id)}>Cancel</button> : null}
                    <button className="btn-ghost text-[11px] ml-auto" style={{ color: '#B91C1C' }} onClick={async () => { if (window.confirm('Delete PO?')) await deletePO(po.id) }}>Delete</button>
                  </div>
                </Card>
              )
            })}
          </div>
        )}
      </section>

      {creating ? <CreateModal vendors={vendors} products={products} onClose={() => setCreating(false)} /> : null}
      {receiveFor ? <ReceiveModal po={receiveFor} onClose={() => setReceiveFor(null)} /> : null}
    </GShell>
  )
}

function CreateModal({ vendors, products, onClose }) {
  const [vendorId, setVendorId] = useState('')
  const [expectedDate, setExpectedDate] = useState('')
  const [lines, setLines] = useState([{ productId: '', name: '', qty: '', cost: '' }])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const prodList = Object.values(products || {}).filter(Boolean).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  const setLine = (i, patch) => setLines((ls) => ls.map((l, j) => j === i ? { ...l, ...patch } : l))
  const pickProduct = (i, pid) => { const p = products[pid]; setLine(i, { productId: pid, name: p?.name || p?.sku || '', cost: p?.cost ?? '' }) }
  const save = async () => {
    setBusy(true); setErr('')
    try { await createPO({ vendorId, expectedDate, lines: lines.filter((l) => l.name || l.productId) }); onClose() }
    catch (e) { setErr(e?.message || 'Failed'); setBusy(false) }
  }
  return (
    <Modal title="New Purchase Order" onClose={onClose} wide>
      <div className="grid sm:grid-cols-2 gap-2">
        <Field label="Vendor"><select className="input w-full" value={vendorId} onChange={(e) => setVendorId(e.target.value)}><option value="">— select vendor —</option>{Object.values(vendors).filter(Boolean).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</select></Field>
        <Field label="Expected date"><input className="input w-full" type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} /></Field>
      </div>
      <div className="mt-3 text-xs font-bold uppercase tracking-widest text-gray-500">Lines</div>
      {lines.map((l, i) => (
        <div key={i} className="grid grid-cols-12 gap-1 mt-1 items-center">
          <select className="input col-span-5" value={l.productId} onChange={(e) => pickProduct(i, e.target.value)}>
            <option value="">— product / free text —</option>
            {prodList.map((p) => <option key={p.id} value={p.id}>{p.name || p.sku}</option>)}
          </select>
          <input className="input col-span-3" placeholder="Item name" value={l.name} onChange={(e) => setLine(i, { name: e.target.value })} />
          <input className="input col-span-2" type="number" placeholder="Qty" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} />
          <input className="input col-span-2" type="number" step="0.01" placeholder="Cost" value={l.cost} onChange={(e) => setLine(i, { cost: e.target.value })} />
        </div>
      ))}
      <button className="btn-ghost text-xs mt-1" onClick={() => setLines((ls) => [...ls, { productId: '', name: '', qty: '', cost: '' }])}>+ Add line</button>
      <div className="flex justify-end gap-2 mt-3"><button className="btn-ghost text-sm" onClick={onClose}>Cancel</button><button className="btn-primary text-sm" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Create PO'}</button></div>
      <Err msg={err} />
    </Modal>
  )
}

function ReceiveModal({ po, onClose }) {
  const lines = Object.entries(po.lines || {})
  const [rec, setRec] = useState({})
  const [busy, setBusy] = useState(false)
  const save = async () => { setBusy(true); try { await receivePO(po.id, rec); onClose() } catch { setBusy(false) } }
  return (
    <Modal title="Receive PO" onClose={onClose}>
      <p className="text-xs text-gray-500 mb-2">Enter quantity received now per line — inventory updates automatically.</p>
      {lines.map(([id, l]) => (
        <div key={id} className="flex items-center justify-between gap-2 mb-1">
          <span className="text-sm">{l.name} <span className="text-gray-400 text-xs">({l.received || 0}/{l.qty})</span></span>
          <input className="input w-24" type="number" placeholder="qty" value={rec[id] || ''} onChange={(e) => setRec((r) => ({ ...r, [id]: e.target.value }))} />
        </div>
      ))}
      <div className="flex justify-end gap-2 mt-3"><button className="btn-ghost text-sm" onClick={onClose}>Cancel</button><button className="btn-primary text-sm" onClick={save} disabled={busy}>{busy ? 'Receiving…' : 'Receive'}</button></div>
    </Modal>
  )
}

function printPO(po, vendorName) {
  const lines = Object.values(po.lines || {})
  const w = window.open('', '_blank', 'width=700,height=800')
  w.document.write(`<div style="font-family:sans-serif;padding:24px">
    <h2>Purchase Order</h2><p><b>Vendor:</b> ${vendorName}<br><b>Status:</b> ${po.status}${po.expectedDate ? `<br><b>Expected:</b> ${po.expectedDate}` : ''}</p>
    <table style="width:100%;border-collapse:collapse" border="1" cellpadding="6">
      <tr><th align="left">Item</th><th>Qty</th><th>Cost</th><th>Line</th></tr>
      ${lines.map((l) => `<tr><td>${l.name}</td><td align="center">${l.qty}</td><td align="right">$${(Number(l.cost) || 0).toFixed(2)}</td><td align="right">$${((Number(l.qty) || 0) * (Number(l.cost) || 0)).toFixed(2)}</td></tr>`).join('')}
      <tr><td colspan="3" align="right"><b>Total</b></td><td align="right"><b>$${lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.cost) || 0), 0).toFixed(2)}</b></td></tr>
    </table></div>`)
  w.document.close(); w.focus(); w.print()
}
